import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, Input, ViewChild, inject } from '@angular/core';
import { isNumber } from '@datorama/akita';
import { Observable, finalize, forkJoin, from, of, switchMap, throwError } from 'rxjs';
import { NgxImageCompressService } from 'ngx-image-compress';
import { TranslateService } from '@ngx-translate/core';
import { formatDate } from '@angular/common';
import { FormControl, FormGroup } from '@angular/forms';
import { ServiceBase, SnackbarFactory } from '@shared/services';
import { ConstrainedField } from '@shared/models/constrained-field';

@Component({
  selector: 'app-file-cell',
  templateUrl: './file-cell.component.html',
  styleUrls: ['./file-cell.component.scss']
})
export class FileCellComponent {
  @Input() public field!: ConstrainedField;

  private service = inject(ServiceBase);
  private snackBar = inject(SnackbarFactory);

  Object = Object;

  public key: string;
  public nameKey: string;
  public uploadNameKey: string;

  private newMegabyteSum: number = 0;
  private readonly fullStopsExceptExtension: RegExp = /\.(?=.*\.)/g;
  private readonly fullStopsAndWhitespaceExceptExtension: RegExp = /[\.\s](?=.*\.)/g;
  private readonly everySpecialCharExceptExtension: RegExp = /[\W\s](?=.*\.)/g;

  public readonly imageExtensions = ['png', 'jpg', 'jpeg', 'webp'];
  public readonly acceptableExtensions = [...this.imageExtensions, 'pdf', 'doc', 'docx', 'csv', 'xlsx', 'txt', 'msg', 'rtf'];  //msg and rtf were already in the database as accepted uploaded files

  public processingFiles: boolean = false;
  public formGroup = this.field?.control as FormGroup;

  public fileControl: FormControl<any>;
  public fileNameControl: FormControl<any>;
  public fileUploadNameControl: FormControl<any>;

  @ViewChild('fileInput') fileInput;

  constructor(
    private translateService: TranslateService,
    private imageCompress: NgxImageCompressService,
  ) { }

  ngOnInit() {
    this.key= this.field?.key ?? '';
    this.nameKey = `${this.key}Name`;
    this.uploadNameKey = `${this.key}UploadName`;

    this.formGroup = this.field?.control as FormGroup;

    this.fileControl = this.formGroup?.controls[this.key] as FormControl;
    this.fileNameControl = this.formGroup?.controls[this.nameKey] as FormControl;
    this.fileUploadNameControl = this.formGroup?.controls[this.uploadNameKey] as FormControl;
  }

  public toDbModel(value: any): any {
    let readValue = [];

    let arrayFields = [this.key, this.nameKey, this.uploadNameKey];

    for (var i = 0; i < value[this.key]?.length; i++) {
      let row = {};
      arrayFields.forEach(k => {
        row[k] = value[k][i];
      })
      readValue.push(row);
    }

    return readValue;
  }

  onFileInput(): void {
    this.processingFiles = true;
    const files = this.fileInput.nativeElement.files as FileList;
    const MAX_MEGABYTE = 5;
    const MAX_SUM_MEGABYTE = 30;

    let observableList: Observable<File>[] = [];

    let formFiles = this.fileControl.value ?? [];
    let formFileNames = this.fileNameControl.value ?? [];
    let formFileUploadNames = this.fileUploadNameControl.value ?? [];

    for (var i = 0; i < files.length; i++) {
      let f = files[i];
      if (formFiles.filter((fi) => fi.name === f.name).length === 0) {
        const safeName = f.name.replaceAll(this.fullStopsAndWhitespaceExceptExtension, "_");
        let splitName = safeName.split('.');
        let fileExtension = splitName[1]?.toLowerCase();
        if (!this.acceptableExtensions.includes(fileExtension)) {
          observableList.push(throwError(() => {
            let unacepptableTxt = this.translateService.instant('unaccpetable-file-extension');
            let error = new HttpErrorResponse({ statusText: `${unacepptableTxt}: ${splitName[1] ?? f.name}`, status: 406 });
            return error;
          }));
        }
        else {
          var totalSizeMb = f.size / Math.pow(1024, 2);
          if (this.imageExtensions.includes(fileExtension) && totalSizeMb > MAX_MEGABYTE) {
            let ratio = Math.ceil((MAX_MEGABYTE * 100) / totalSizeMb)
            observableList.push(
              this.getBase64(f).pipe(
                switchMap(image => {
                  return from(this.imageCompress.compressFile((image as string), -2, ratio));
                }),
                switchMap(compressedImage => {
                  return from(this.dataUrlToFile(compressedImage, f.name, f.type));
                }),
              )
            );
          }
          else {
            observableList.push(of(f));
          }
        }

      }
    }

    forkJoin(observableList).pipe(
      finalize(() => {
        this.processingFiles = false;
      })
    ).subscribe({
      next: newFiles => {
        let megabyteSum = this.newMegabyteSum;
        [...formFiles, ...newFiles].forEach(f => megabyteSum += f.size / Math.pow(1024, 2));

        if (megabyteSum > MAX_SUM_MEGABYTE) {
          this.snackBar.showButtonSnackbarWithText(400, "tooManyFiles");
          this.translateService.get("tooManyFiles").subscribe(
            txt => {
              this.snackBar.showButtonSnackbarWithText(400, `${txt} | ${megabyteSum.toFixed(2)}MB > ${MAX_SUM_MEGABYTE}MB`);
            }
          )
        }
        else {
          for (var i = 0; i < newFiles.length; i++) {
            let file = newFiles[i];
            const safeName = file.name.replaceAll(this.fullStopsAndWhitespaceExceptExtension, "_");
            let splitName = safeName.split('.');
            let fileUploadName = `${splitName[0]}_${formatDate(new Date(), 'yyyyMMdd_HHmmss', 'en')}.${splitName[1]}`;
            formFiles.push(file);
            formFileNames.push(file.name);
            formFileUploadNames.push(fileUploadName);
          }

          this.fileControl.patchValue(formFiles);
          this.fileNameControl.patchValue(formFileNames);
          this.fileUploadNameControl.patchValue(formFileUploadNames);
        }
      },
      error: (err) => {
        if (err.status) {
          this.snackBar.showButtonSnackbarWithText(err.status, err.statusText);
        }
        else {
          console.log(err);
          this.snackBar.showButtonSnackbarWithText(400, "unknown-error, check console");
        }
      }
    });

  }

  getBase64(file): Observable<string | ArrayBuffer> {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Observable((observer) => {
      reader.onload = () => { observer.next(reader.result); observer.complete(); };
      reader.onerror = error => observer.error(error);
    });
  }

  async dataUrlToFile(dataUrl: string, fileName: string, type: string): Promise<File> {
    const res: Response = await fetch(dataUrl);
    const blob: Blob = await res.blob();
    return new File([blob], fileName, { type });
  }

  onDeleteFileInput(index) {
    let filter = (f, i) => i !== index;

    let formFile = this.fileControl.value.filter(filter);
    let formFileName = this.fileNameControl.value.filter(filter);
    let formFileUploadName = this.fileUploadNameControl.value.filter(filter);

    this.fileControl.patchValue(formFile);
    this.fileNameControl.patchValue(formFileName);
    this.fileUploadNameControl.patchValue(formFileUploadName);
  }

  onClickFileInputButton(e): void {
    e.preventDefault();
    this.fileInput.nativeElement.click();
  }

  getFileInputAcceptString(): string {
    return this.acceptableExtensions.map(ext => `.${ext}`).join(', ');
  }
}
